import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import { Avatar, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

import { useMentionUser } from '../hooks/useMentionUser';
import type { MentionHoverCardProps } from './types';

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function MentionHoverCard({
  userId,
  displayName,
  email,
  role,
  department,
  avatar,
  isDeactivated,
  entityId,
  disableAutoFetch = false,
  anchorEl,
  open,
  onClose,
  sx,
}: MentionHoverCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Lazy fetch: only hit the network when the card opens AND auto-fetch is
  // not disabled. Passing undefined makes the hook a no-op.
  const shouldFetch = open && !disableAutoFetch;
  const { user: fetchedUser, isLoading } = useMentionUser(
    shouldFetch ? userId : undefined,
    entityId,
  );

  const resolvedDisplayName =
    displayName && displayName.length > 0 ? displayName : (fetchedUser?.fullName ?? '');
  const resolvedEmail = email ?? fetchedUser?.email;
  const resolvedRole = role ?? fetchedUser?.role.displayName;
  const resolvedDepartment = department ?? fetchedUser?.department ?? undefined;
  const resolvedAvatar = avatar !== undefined ? avatar : fetchedUser?.avatar;
  const resolvedIsDeactivated = isDeactivated ?? fetchedUser?.disabled ?? false;

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    elements: { reference: anchorEl },
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({ fallbackPlacements: ['top-start'] }), shift({ padding: 8 })],
  });

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(e.target as Node) &&
        !anchorEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose, anchorEl]);

  if (!open) return null;

  const roleAndDept = [resolvedRole, resolvedDepartment].filter(Boolean).join(' \u00b7 ');
  const showSkeleton = isLoading && resolvedDisplayName.length === 0;

  return (
    <Paper
      ref={(node: HTMLDivElement | null) => {
        refs.setFloating(node);
        (cardRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      style={floatingStyles}
      elevation={8}
      sx={[
        { p: 1.5, minWidth: 240, maxWidth: 320, zIndex: 'tooltip' },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {showSkeleton ? (
          <Skeleton variant="circular" width={40} height={40} />
        ) : (
          <Avatar
            {...(resolvedAvatar != null ? { src: resolvedAvatar } : {})}
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.light',
              color: 'primary.dark',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {!resolvedAvatar && getInitials(resolvedDisplayName)}
          </Avatar>
        )}

        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          {showSkeleton ? (
            <>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="50%" />
            </>
          ) : (
            <>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" noWrap>
                  {resolvedDisplayName}
                </Typography>
                {resolvedIsDeactivated && (
                  <Chip
                    label="Deactivated"
                    size="small"
                    color="error"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Stack>
              {typeof resolvedEmail === 'string' && resolvedEmail.length > 0 && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {resolvedEmail}
                </Typography>
              )}
              {roleAndDept && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {roleAndDept}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
